import {
  useMsalAuthentication
} from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { ReactNode, useEffect, useState } from "react";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { Button } from "../../ui/components/Button";
import { useAdminDispatch, useAdminSelector } from "../../../stores/AdminStore";
import { signIn } from "../../../stores/AuthSlice";
import { Loading } from "../../../../store/Loading";
import {useLocalStorage} from "@unleashed/hooks/useLocalStorage";

interface AuthenticationGuardProps {
  children?: ReactNode | ReactNode[];
}

export const AuthenticationGuard = ({children}: AuthenticationGuardProps) => {
  const dispatch = useAdminDispatch();
  const storage = useLocalStorage();
  const config = useAdminSelector(s => s.config);
  const {status} = useAdminSelector(s => s.auth);
  const {
    login,
    result,
    acquireToken
  } = useMsalAuthentication(InteractionType.Redirect, {scopes: [
    "openid",
    "offline_access",
    import.meta.env.VITE_AZURE_B2C_APP_ID,
  ]});
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    if (status == "unauthenticated") {
      setTimeout(() => {
        setShowLogin(true);
      }, 5000);
    }
  });

  useEffect(() => {
    if (!result?.account || status !== "unauthenticated") {
      return;
    }

    const name = result?.account.name ?? "Unknown";
    const username = result?.account.username ?? "Unknown";
    const names = name.split(" ");
    const initials = names && names?.length > 1
      ? `${names[0].substring(0, 1)}${names[1].substring(0, 1)}`
      : "UK";

    const lastActiveDateKey = `${import.meta.env.MODE}_last_active_date`;
    const lastActiveDate = storage.get<number>(lastActiveDateKey);
    // eslint-disable-next-line
    const claims:any = result.idTokenClaims;

    if (lastActiveDate && claims.auth_time) {
      const timeLapse = claims.auth_time * 1000 - lastActiveDate;

      if (timeLapse < config.timeoutInSeconds * 1000) {
        storage.remove(lastActiveDateKey);
      }
      else if (claims.idp && timeLapse >= config.timeoutInSeconds * 1000) {
        // third-party identity provider (IdP): "https://login.microsoftonline.com/..."
        storage.remove(lastActiveDateKey);
      }
    }

    dispatch(signIn({
      name,
      username,
      initials,
      accessToken: result.accessToken
    }));
  }, [acquireToken, config.timeoutInSeconds, dispatch, result, status, storage]);

  const handleSignIn = async () => {
    await login();
  };

  if (status == "unauthenticated") {
    return showLogin ? (
      <div className="flex justify-center items-center min-h-screen">
        <Button title={t`Sign In`} onClick={handleSignIn}>
          <Trans>Sign In</Trans>
        </Button>
      </div>
    ) : <Loading/>;
  }

  return (
    <>
      {children}
    </>
  );
};
